import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import Cookies from "js-cookie";
import React, { useState } from "react";
import { assistances } from "./Interaction";

import ThriveModal from "../common/ThriveModal";
import Confirm from "./Confirm";
import ReferralStatusChip, {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusLabels,
  isTerminalPipelineStatus,
} from "../common/ReferralStatusChip";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import EmailEditor from "../common/EmailEditor";
import { ResourceReferralClientSurvey } from "../common/EmailBodies/ResourceReferralClientSurvey";
import { StyledTextarea } from "../common/Textareas";
import { axiosInstance } from "../../utils/networking";

const Referral = (props: any) => {
  const [surveyEditorOpen, setSurveyEditorOpen] = useState<boolean>(false);
  const token: string | undefined = Cookies.get("token");
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`, // Add JWT token to Authorization header
    },
  };
  const [referralContent, setReferralContent] = React.useState<any>({
    resource_id: "",
    provider_id: "",
    referral_objective: "No Response",
    referral_note: "",
    case_id: "",
    _id: "",
    referral_date: dayjs(),
    status: "active",
    pipeline_status: "referred",
    status_history: [],
    outcome: null,
  });
  const [statusNote, setStatusNote] = useState<string>("");
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState<boolean>(false);
  const [pendingStatus, setPendingStatus] = useState<string>("");
  const [outcomeDraft, setOutcomeDraft] = useState<any>({
    assistance_provided: null,
    objective_achieved: null,
    outcome_note: "",
  });
  const [resource, setResource] = React.useState({
    id: "",
    title: "",
    description: "",
    url: "",
    tags: "",
    imagePath: "",
    orgName: "",
    address: "",
    email: "",
    firstName: "",
    lastName: "",
  });

  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = React.useState<string>("");
  const [confirmAction, setConfirmAction] = React.useState<() => void>(
    () => {}
  );
  const [messageOpen, setMessageOpen] = React.useState<boolean>(false);
  const [messageTitle, setMessageTitle] = React.useState<string>("");
  const [messageBody, setMessageBody] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const getResourceById = (resource_id: any) => {
    axiosInstance.get(`/resources?resource_id=${resource_id}`).then((res) => {
      setResource(res.data.resource);
    });
  };

  const getProviderById = (provider_id: any) => {
    axiosInstance.get(`/resources?provider_id=${provider_id}`).then((res) => {
      setResource(res.data.provider);
    });
  };

  React.useEffect(() => {
    axiosInstance.get(`/referral?referral_id=${props.referral}`).then((res) => {
      setReferralContent({
        ...res.data.referralContent,
      });
      if (res.data.referralContent) {
        if (res.data.referralContent.resource_id) {
          getResourceById(res.data.referralContent.resource_id);
        }
        if (res.data.referralContent.provider_id) {
          getProviderById(res.data.referralContent.provider_id);
        }
      }
    });
  }, []);

  const handleSaveReferral = () => {
    axiosInstance
      .post(`/referral`, {
        activity_type: "Referral",
        referral: referralContent,
      })
      .then((res) => {
        if (res.status === 200) {
          props.setDataFlag(!props.dataFlag);
        } else {
          console.log("API Error");
        }
      });
  };

  const handleDelete = () => {
    if (referralContent._id) {
      axiosInstance
        .post(`/referral/delete`, {
          activity_type: "Referral",
          referral: referralContent,
        })
        .then((res) => {
          if (res.status === 200) {
            window.location.reload();
          } else {
            setErrorMessage("Failed to unassign the referral. Please try again.");
          }
        })
        .catch(() => {
          setErrorMessage("Failed to unassign the referral. Please try again.");
        });
    }
  };

  const handleReassign = () => {
    if (referralContent._id) {
      axiosInstance
        .post(`/referral/reassign`, {
          activity_type: "Referral",
          referral: referralContent,
        })
        .then((res) => {
          if (res.status === 200) {
            window.location.reload();
          } else {
            setErrorMessage("Failed to reassign the referral. Please try again.");
          }
        })
        .catch(() => {
          setErrorMessage("Failed to reassign the referral. Please try again.");
        });
    }
  };

  const postStatusUpdate = (newStatus: string, outcome?: any) => {
    axiosInstance
      .post(`/referral/status`, {
        referral_id: referralContent._id,
        pipeline_status: newStatus,
        note: statusNote || undefined,
        outcome: outcome,
      })
      .then((res) => {
        if (res.status === 200) {
          setReferralContent({
            ...referralContent,
            pipeline_status: newStatus,
            outcome: outcome ? outcome : referralContent.outcome,
            status_history: [
              ...(referralContent.status_history || []),
              {
                status: newStatus,
                changed_at: new Date(),
                changed_by: "you",
                note: statusNote || undefined,
              },
            ],
          });
          setStatusNote("");
          props.setDataFlag(!props.dataFlag);
        }
      })
      .catch(() => {
        setErrorMessage("Failed to update referral status. Please try again.");
      });
  };

  const handleStatusSelect = (newStatus: string) => {
    if (newStatus === referralContent.pipeline_status) {
      return;
    }
    if (isTerminalPipelineStatus(newStatus)) {
      setPendingStatus(newStatus);
      setOutcomeDraft({
        assistance_provided: null,
        objective_achieved: null,
        outcome_note: "",
      });
      setOutcomeDialogOpen(true);
    } else {
      postStatusUpdate(newStatus);
    }
  };

  const client_survey_result = (referral_id: any) => {
    if (referralContent) {
      let client_survey_activity = props.activity.find((item: any) => {
        return (
          item.activity_type === "Client Referral Survey (by Client)" &&
          item.referral_id === referral_id
        );
      });
      if (client_survey_activity) {
        return client_survey_activity._id === referralContent._id;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  const client_notice_sent = () => {
    let client_referral_notice_activity = props.activity.find((item: any) => {
      return (
        (item.activity_type === "Referral Notice" ||
          item.activity_type === "Provider Referral Notice Email") &&
        item.referral_id === referralContent._id
      );
    });

    if (client_referral_notice_activity) {
      return true;
    } else {
      return false;
    }
  };

  const b2b_client_survey_result = (referral_id: any) => {
    let client_survey_activity = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey (by Client)" &&
        item.referral_id === referral_id
      );
    });
    if (client_survey_activity) {
      return true;
    } else {
      return false;
    }
  };

  const b2b_client_survey_button_color = (referral_id: any) => {
    let client_survey_sent = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey Email" &&
        item.referral_id === referral_id
      );
    });
    let client_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey (by Client)" &&
        item.referral_id === referral_id
      );
    });
    if (client_survey_completed) {
      return "success";
    } else if (client_survey_sent) {
      return "warning";
    } else {
      return "primary";
    }
  };

  const b2b_client_survey_button_variant = (referral_id: any) => {
    let client_survey_sent = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey Email" &&
        item.referral_id === referral_id
      );
    });
    let client_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey (by Client)" &&
        item.referral_id === referral_id
      );
    });
    if (client_survey_completed) {
      return "contained";
    } else if (client_survey_sent) {
      return "contained";
    } else {
      return "outlined";
    }
  };

  const b2b_client_survey_button_text = (referral_id: any) => {
    let client_survey_sent = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey Email" &&
        item.referral_id === referral_id
      );
    });
    let client_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey (by Client)" &&
        item.referral_id === referral_id
      );
    });
    if (client_survey_completed) {
      return "B2B Client Survey Complete";
    } else if (client_survey_sent) {
      return "B2B Client Survey Sent";
    } else {
      return "Send Client Survey";
    }
  };

  const ButtonGroupForResourceReferral = (button_props: any) => {
    return referralContent.status == "open" ? (
      <>
        <Button
          size="small"
          variant={client_notice_sent() ? "contained" : "outlined"}
          color={client_notice_sent() ? "success" : "primary"}
          sx={{ whiteSpace: "nowrap" }}
          onClick={() => {
            props.setReferralNoticeOpen(true);
            props.setOpenReferral(resource);
            props.setReferralNoticeContent(referralContent);
          }}
        >
          {client_notice_sent()
            ? "Referral Notice Sent"
            : "Send Referral Notice"}
        </Button>

        {client_survey_result(button_props.referral_id) ? (
          <>
            <Button
              size="small"
              variant="outlined"
              sx={{ whiteSpace: "nowrap" }}
              onClick={() => setSurveyEditorOpen(true)}
            >
              Client Referral Survey
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              color="success"
              variant="contained"
              sx={{ whiteSpace: "nowrap" }}
              onClick={() => {
                let client_survey_activity = props.activity.find(
                  (item: any) => {
                    return (
                      item.activity_type ===
                      "Client Referral Survey (by Client)"
                    );
                  }
                );
                props.setActivityCardOpen(true);
                props.setOpenActivityID(client_survey_activity._id);
              }}
            >
              View Referral Survey
            </Button>
          </>
        )}
      </>
    ) : (
      <></>
    );
  };

  const b2b_provider_survey_result = (referral_id: any) => {
    let provider_survey_activity = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey (by Provider)" &&
        item.referral_id === referral_id
      );
    });
    if (provider_survey_activity) {
      return true;
    } else {
      return false;
    }
  };

  const b2b_provider_survey_button_color = (referral_id: any) => {
    let b2b_provider_survey_sent = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey Email" &&
        item.referral_id === referral_id
      );
    });
    let b2b_provider_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey (by Provider)" &&
        item.referral_id === referral_id
      );
    });
    if (b2b_provider_survey_completed) {
      return "success";
    } else if (b2b_provider_survey_sent) {
      return "warning";
    } else {
      return "primary";
    }
  };

  const b2b_provider_survey_button_variant = (referral_id: any) => {
    let b2b_provider_survey_sent = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey Email" &&
        item.referral_id === referral_id
      );
    });
    let b2b_provider_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey (by Provider)" &&
        item.referral_id === referral_id
      );
    });
    if (b2b_provider_survey_completed) {
      return "contained";
    } else if (b2b_provider_survey_sent) {
      return "contained";
    } else {
      return "outlined";
    }
  };

  const b2b_provider_survey_button_text = (referral_id: any) => {
    let b2b_provider_survey_sent = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey Email" &&
        item.referral_id === referral_id
      );
    });
    let b2b_provider_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey (by Provider)" &&
        item.referral_id === referral_id
      );
    });
    if (b2b_provider_survey_completed) {
      return "B2B Provider Survey Complete";
    } else if (b2b_provider_survey_sent) {
      return "B2B Provider Survey Sent";
    } else {
      return "Send B2B Provider Survey";
    }
  };

  const openB2BProviderSurvey = () => {
    let b2b_provider_survey_activity = props.activity.find((item: any) => {
      return item.activity_type === "B2B Provider Survey (by Provider)";
    });
    props.setActivityCardOpen(true);
    props.setOpenActivityID(b2b_provider_survey_activity.activity_id);
  };

  const openB2BProviderSurveyEmail = () => {
    props.setProviderSurveyOpen(true);
    props.setReferralNoticeContent(referralContent);
    props.setSelectedProvider(resource);
  };

  const openB2BClientSurvey = () => {
    let b2b_client_survey_activity = props.activity.find((item: any) => {
      return item.activity_type === "B2B Client Survey (by Client)";
    });

    props.setActivityCardOpen(true);
    props.setOpenActivityID(b2b_client_survey_activity.activity_id);
  };

  const openB2BClientSurveyEmail = () => {
    props.setClientSurveyOpen(true);
    props.setReferralNoticeContent(referralContent);
  };

  const ButtonGroupForProviderReferral = (button_props: any) => {
    return referralContent.status == "open" ? (
      <>
        <Button
          size="small"
          color={client_notice_sent() ? "success" : "primary"}
          variant={client_notice_sent() ? "contained" : "outlined"}
          sx={{ whiteSpace: "nowrap" }}
          onClick={() => {
            props.setProviderReferralNoticeOpen(true);
            props.setReferralNoticeContent(referralContent);
          }}
        >
          {client_notice_sent()
            ? "Provider Notice Sent"
            : "Send Provider Notice"}
        </Button>
        <Button
          size="small"
          color={b2b_client_survey_button_color(button_props.referral_id)}
          variant={b2b_client_survey_button_variant(button_props.referral_id)}
          sx={{ whiteSpace: "nowrap" }}
          onClick={() => {
            b2b_client_survey_result(button_props.referral_id)
              ? openB2BClientSurvey()
              : openB2BClientSurveyEmail();
          }}
        >
          {b2b_client_survey_button_text(button_props.referral_id)}
        </Button>
        <Button
          size="small"
          color={b2b_provider_survey_button_color(button_props.referral_id)}
          variant={b2b_provider_survey_button_variant(button_props.referral_id)}
          sx={{ whiteSpace: "nowrap" }}
          onClick={() => {
            b2b_provider_survey_result(button_props.referral_id)
              ? openB2BProviderSurvey()
              : openB2BProviderSurveyEmail();
          }}
        >
          {b2b_provider_survey_button_text(button_props.referral_id)}
        </Button>
      </>
    ) : (
      <></>
    );
  };

  const provider_referral_completion_indicator = (referral_id: any) => {
    let b2b_provider_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Provider Survey (by Provider)" &&
        item.referral_id === referral_id
      );
    });
    let client_survey_completed = props.activity.find((item: any) => {
      return (
        item.activity_type === "B2B Client Survey (by Client)" &&
        item.referral_id === referral_id
      );
    });

    if (b2b_provider_survey_completed && client_survey_completed) {
      return `B2B Engagement Completed (${
        new Date(b2b_provider_survey_completed.created_at) >
        new Date(client_survey_completed.created_at)
          ? new Date(b2b_provider_survey_completed.created_at).toLocaleString(
              "en-US",
              {
                timeZone: "EST",
              }
            )
          : new Date(client_survey_completed.created_at).toLocaleString(
              "en-US",
              {
                timeZone: "EST",
              }
            )
      })`;
    } else if (b2b_provider_survey_completed) {
      return "B2B Provider Survey Completed";
    } else if (client_survey_completed) {
      return "B2B Client Survey Completed";
    } else {
      return "Referral Processing";
    }
  };
  return (
    <>
      {/* ---- Header ---------------------------------------------------- */}
      <Box
        sx={{
          height: "4px",
          backgroundColor: referralContent["resource_id"]
            ? "#fdb814"
            : "#9E1F63FF",
        }}
      />
      <Box
        sx={{
          padding: "16px 20px",
          borderBottom: "1px solid #ececf0",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}
            >
              {resource["orgName"] ||
                referralContent["referred_to"] ||
                "Referral"}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ marginTop: "4px" }}
            >
              <Typography variant="caption" sx={{ color: "#8a8a93" }}>
                {referralContent["resource_id"] ? "Resource" : "Provider"}{" "}
                Referral
              </Typography>
              <ReferralStatusChip
                status={referralContent.pipeline_status}
                size="small"
              />
            </Stack>
          </Box>
          {resource.imagePath ? (
            <Box sx={{ flexShrink: 0 }}>
              <img
                src={resource.imagePath}
                alt={resource["orgName"] || "Referral partner"}
                style={{
                  maxHeight: "56px",
                  maxWidth: "160px",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          ) : null}
        </Stack>
      </Box>

      {/* ---- Pipeline -------------------------------------------------- */}
      <Box
        sx={{
          padding: "16px 20px",
          borderBottom: "1px solid #ececf0",
        }}
      >
        <Stepper
          activeStep={[
            "referred",
            "appointment_scheduled",
            "in_progress",
            "completed",
          ].indexOf(referralContent.pipeline_status || "referred")}
          alternativeLabel
          sx={(theme) => ({
            width: "100%",
            // Phones: tighter steps and smaller labels so all four fit.
            [theme.breakpoints.down("sm")]: {
              "& .MuiStep-root": { paddingX: "4px" },
              "& .MuiStepLabel-label": { fontSize: "0.75rem" },
            },
          })}
        >
          {["referred", "appointment_scheduled", "in_progress", "completed"].map(
            (step) => (
              <Step
                key={step}
                completed={
                  ["referred", "appointment_scheduled", "in_progress", "completed"].indexOf(
                    referralContent.pipeline_status || "referred"
                  ) > ["referred", "appointment_scheduled", "in_progress", "completed"].indexOf(step) ||
                  referralContent.pipeline_status === "completed"
                }
              >
                <StepLabel>{pipelineStatusLabels[step]}</StepLabel>
              </Step>
            )
          )}
        </Stepper>
        {(referralContent.pipeline_status === "unsuccessful" ||
          referralContent.pipeline_status === "follow_up_needed") && (
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", color: "#8a8a93" }}
          >
            {provider_referral_completion_indicator(referralContent._id)}
          </Typography>
        )}
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{ gap: "12px", marginTop: "12px" }}
        >
          {referralContent.status == "open" && (
            <>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="referral-status-select-label">
                  Update Status
                </InputLabel>
                <Select
                  labelId="referral-status-select-label"
                  label="Update Status"
                  value={referralContent.pipeline_status || "referred"}
                  onChange={(e: any) => handleStatusSelect(e.target.value)}
                >
                  {REFERRAL_PIPELINE_STATUSES.map((statusOption) => (
                    <MenuItem key={statusOption} value={statusOption}>
                      {pipelineStatusLabels[statusOption]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                label="Status note (optional)"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
              />
            </>
          )}
          <Button
            size="small"
            sx={{ whiteSpace: "nowrap" }}
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            {historyOpen ? "Hide History" : "History"}
          </Button>
        </Stack>
        <Collapse in={historyOpen}>
          <Box sx={{ padding: "8px 4px" }}>
            {(referralContent.status_history || []).length === 0 ? (
              <Typography variant="body2" sx={{ color: "#8a8a93" }}>
                No status changes recorded yet.
              </Typography>
            ) : (
              [...(referralContent.status_history || [])]
                .reverse()
                .map((entry: any, index: number) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ padding: "2px 0" }}
                  >
                    <ReferralStatusChip status={entry.status} />
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      {new Date(entry.changed_at).toLocaleString("en-US", {
                        timeZone: "EST",
                      })}
                      {entry.changed_by === "system"
                        ? " · automatic"
                        : entry.changed_by === "migration"
                        ? " · migrated"
                        : ""}
                      {entry.note ? ` — ${entry.note}` : ""}
                    </Typography>
                  </Stack>
                ))
            )}
            {referralContent.outcome && (
              <Typography variant="body2" sx={{ marginTop: "6px" }}>
                <b>Outcome:</b> Assistance provided:{" "}
                {referralContent.outcome.assistance_provided === true
                  ? "Yes"
                  : referralContent.outcome.assistance_provided === false
                  ? "No"
                  : "Unknown"}
                {" · "}Objective achieved:{" "}
                {referralContent.outcome.objective_achieved === true
                  ? "Yes"
                  : referralContent.outcome.objective_achieved === false
                  ? "No"
                  : "Unknown"}
                {referralContent.outcome.outcome_note
                  ? ` — ${referralContent.outcome.outcome_note}`
                  : ""}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* ---- Details --------------------------------------------------- */}
      <Box sx={{ padding: "16px 20px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: "100%" }}
                label="Referral Date"
                value={dayjs(referralContent.referral_date)}
                onChange={(value) => {
                  setReferralContent({
                    ...referralContent,
                    referral_date: value as Dayjs,
                  });
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel id="referral-objective-select-label">
                Referral Objective
              </InputLabel>
              <Select
                labelId="referral-objective-select-label"
                label="Referral Objective"
                onChange={(e: any) => {
                  setReferralContent({
                    ...referralContent,
                    referral_objective: e.target.value,
                  });
                }}
                value={referralContent.referral_objective}
              >
                {assistances.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <StyledTextarea
              sx={{ width: "100%", boxSizing: "border-box" }}
              minRows={5}
              maxRows={10}
              placeholder="Referral Notes"
              onChange={(e: any) => {
                setReferralContent({
                  ...referralContent,
                  referral_note: e.target.value,
                });
              }}
              value={referralContent.referral_note}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ---- Action bar ------------------------------------------------ */}
      <Box
        sx={{
          padding: "12px 20px",
          borderTop: "1px solid #ececf0",
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{ gap: "8px" }}
        >
          {referralContent["resource_id"] ? (
            <ButtonGroupForResourceReferral
              referral_id={referralContent["_id"]}
            ></ButtonGroupForResourceReferral>
          ) : (
            <ButtonGroupForProviderReferral
              referral_id={referralContent["_id"]}
            ></ButtonGroupForProviderReferral>
          )}
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ gap: "8px", marginLeft: "auto" }}
          >
            {referralContent["status"] == "open" ? (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={() => {
                    setConfirmMessage("Are you sure to delete the Referral?");
                    setConfirmOpen(true);
                    setConfirmAction(() => handleDelete);
                  }}
                >
                  Unassign
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  sx={{
                    whiteSpace: "nowrap",
                    backgroundColor: "#9E1F63FF",
                  }}
                  onClick={handleSaveReferral}
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="success"
                size="small"
                sx={{ whiteSpace: "nowrap" }}
                onClick={() => {
                  setConfirmMessage("Reassign the Referral?");
                  setConfirmOpen(true);
                  setConfirmAction(() => handleReassign);
                }}
              >
                Assign Back
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* ---- Footer ---------------------------------------------------- */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          padding: "4px 20px 8px",
          color: "text.secondary",
          textAlign: "left",
        }}
      >
        id: {props.referral}
      </Typography>
      <ThriveModal
        height={750}
        open={surveyEditorOpen}
        setOpen={setSurveyEditorOpen}
        contentComponent={
          <EmailEditor
            setOpen={setSurveyEditorOpen}
            theCase={props.case}
            theFirm={props.firm}
            theReferral={referralContent}
            theSubject={
              "Oakland Thrive - Referral Survey for " +
              props.firm["Company Name"]
            }
            editorTitle={"Client Survey - Resource"}
            bodyGenerator={ResourceReferralClientSurvey}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            theBFC={{}}
            theSelectedEvent={{}}
            theSelectedProvider={{}}
          ></EmailEditor>
        }
      ></ThriveModal>
      <Confirm
        isOpen={confirmOpen}
        setCardOpen={setConfirmOpen}
        message={confirmMessage}
        confirmAction={confirmAction}
      />
      <Dialog
        open={outcomeDialogOpen}
        onClose={() => setOutcomeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Record Outcome —{" "}
          {pendingStatus ? pipelineStatusLabels[pendingStatus] : ""}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "8px" }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="assistance-provided-label">
                Was assistance provided?
              </InputLabel>
              <Select
                labelId="assistance-provided-label"
                label="Was assistance provided?"
                value={
                  outcomeDraft.assistance_provided === null
                    ? "unknown"
                    : outcomeDraft.assistance_provided
                    ? "yes"
                    : "no"
                }
                onChange={(e: any) =>
                  setOutcomeDraft({
                    ...outcomeDraft,
                    assistance_provided:
                      e.target.value === "unknown"
                        ? null
                        : e.target.value === "yes",
                  })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="objective-achieved-label">
                Was the objective achieved?
              </InputLabel>
              <Select
                labelId="objective-achieved-label"
                label="Was the objective achieved?"
                value={
                  outcomeDraft.objective_achieved === null
                    ? "unknown"
                    : outcomeDraft.objective_achieved
                    ? "yes"
                    : "no"
                }
                onChange={(e: any) =>
                  setOutcomeDraft({
                    ...outcomeDraft,
                    objective_achieved:
                      e.target.value === "unknown"
                        ? null
                        : e.target.value === "yes",
                  })
                }
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Outcome note (optional)"
              value={outcomeDraft.outcome_note}
              onChange={(e) =>
                setOutcomeDraft({
                  ...outcomeDraft,
                  outcome_note: e.target.value,
                })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutcomeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#9E1F63FF" }}
            onClick={() => {
              postStatusUpdate(pendingStatus, outcomeDraft);
              setOutcomeDialogOpen(false);
            }}
          >
            Save Status & Outcome
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage("")}
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Referral;
